import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// Async thunks
export const fetchProjects = createAsyncThunk(
	"projects/fetchAll",
	async (workspaceId) => {
		const response = await api.get("/projects", { params: { workspaceId } });
		return response.data;
	},
);

export const createProject = createAsyncThunk(
	"projects/create",
	async (project, { rejectWithValue }) => {
		try {
			const response = await api.post("/projects", project);
			return response.data;
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to create project";
			return rejectWithValue(message);
		}
	},
);

export const updateProject = createAsyncThunk(
	"projects/update",
	async ({ id, project }) => {
		const response = await api.put(`/projects/${id}`, project);
		return response.data;
	},
);

export const deleteProject = createAsyncThunk(
	"projects/delete",
	async (id, { rejectWithValue }) => {
		try {
			await api.delete(`/projects/${id}`);
			return id;
		} catch (error) {
			const responseData = error.response?.data;
			let message = "Failed to delete project";

			if (typeof responseData === "string") {
				message = responseData;
			} else if (responseData?.message) {
				message = responseData.message;
			} else if (responseData?.error) {
				message = responseData.error;
			} else if (error.message) {
				message = error.message;
			}

			return rejectWithValue(message);
		}
	},
);

export const archiveProject = createAsyncThunk(
	"projects/archive",
	async (id) => {
		const response = await api.post(`/projects/${id}/archive`, {});
		return response.data;
	},
);

const projectSlice = createSlice({
	name: "projects",
	initialState: {
		projects: [],
		selectedProject: null,
		loading: false,
		error: null,
	},
	reducers: {
		selectProject: (state, action) => {
			state.selectedProject = action.payload;
		},
		clearError: (state) => {
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch projects
			.addCase(fetchProjects.pending, (state) => {
				state.loading = true;
			})
			.addCase(fetchProjects.fulfilled, (state, action) => {
				state.loading = false;
				state.projects = action.payload;
			})
			.addCase(fetchProjects.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})
			// Create project
			.addCase(createProject.fulfilled, (state, action) => {
				state.projects.push(action.payload);
			})
			// Update project
			.addCase(updateProject.fulfilled, (state, action) => {
				const index = state.projects.findIndex(
					(p) => p.id === action.payload.id,
				);
				if (index !== -1) {
					state.projects[index] = action.payload;
				}
				if (state.selectedProject?.id === action.payload.id) {
					state.selectedProject = action.payload;
				}
			})
			// Delete project
			.addCase(deleteProject.fulfilled, (state, action) => {
				state.projects = state.projects.filter(
					(p) => p.id !== action.payload,
				);
				if (state.selectedProject?.id === action.payload) {
					state.selectedProject = null;
				}
			})
			// Archive project
			.addCase(archiveProject.fulfilled, (state, action) => {
				const index = state.projects.findIndex(
					(p) => p.id === action.payload.id,
				);
				if (index !== -1) {
					state.projects[index] = action.payload;
				}
			});
	},
});

export const { selectProject, clearError } = projectSlice.actions;
export default projectSlice.reducer;
